"use client";

import { NextLink } from "cy-fe";
import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/Button";
import { Container } from "@/components/Container";
import { Logo } from "@/components/Logo";
import { NavLinks } from "@/components/NavLinks";
import { urls } from "@/urls";
import { useRegisterStore } from "cy-remittance/registration";
import React from "react";

import { signIn } from "next-auth/react";

function MenuIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path
        d="M5 6h14M5 18h14M5 12h14"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  );
}

function ChevronUpIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path
        d="M17 14l-5-5-5 5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  );
}

function MobileNavLink(
  props: Omit<
    React.ComponentPropsWithoutRef<typeof Popover.Button<typeof NextLink>>,
    "as" | "className"
  >,
) {
  return (
    <PopoverButton
      as={NextLink}
      className="block text-base leading-7 tracking-tight text-gray-700"
      {...props}
    />
  );
}

export function Header() {
  return (
    <header>
      <nav>
        <Container className="relative z-50 flex justify-between py-8">
          <div className="relative z-10 flex items-center gap-16">
            <NextLink aria-label="Home" href="/">
              <Logo className="h-10" />
            </NextLink>
            <div className="hidden lg:flex lg:gap-10">
              <NavLinks />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Popover className="lg:hidden">
              {({ open }) => (
                <>
                  <PopoverButton
                    aria-label="Toggle site navigation"
                    className="ui-not-focus-visible:outline-none relative z-10 -m-2 inline-flex items-center rounded-lg stroke-gray-900 p-2 hover:bg-gray-200/50 hover:stroke-gray-600 active:stroke-gray-900"
                  >
                    {({ open: open2 }) =>
                      open2 ? (
                        <ChevronUpIcon className="h-6 w-6" />
                      ) : (
                        <MenuIcon className="h-6 w-6" />
                      )
                    }
                  </PopoverButton>
                  <AnimatePresence initial={false}>
                    {open ? (
                      <>
                        <PopoverBackdrop
                          animate={{ opacity: 1 }}
                          as={motion.div}
                          exit={{ opacity: 0 }}
                          initial={{ opacity: 0 }}
                          static
                          style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 0,
                            backgroundColor: "rgba(209, 213, 219, 0.6)",
                            backdropFilter: "blur(4px)",
                          }}
                        />
                        <PopoverPanel
                          animate={{ opacity: 1, y: 0 }}
                          as={motion.div}
                          exit={{
                            opacity: 0,
                            y: -32,
                            transition: { duration: 0.2 },
                          }}
                          style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: 0,
                            zIndex: 0,
                            transformOrigin: "top",
                            borderBottomLeftRadius: "1rem",
                            borderBottomRightRadius: "1rem",
                            backgroundColor: "rgb(249, 250, 251)",
                            padding: "8rem 1.5rem 1.5rem 1.5rem",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                          }}
                          initial={{ opacity: 0, y: -32 }}
                          static
                        >
                          <div className="space-y-4">
                            <MobileNavLink href="/#features">
                              Features
                            </MobileNavLink>
                            <MobileNavLink href="/#reviews">
                              Reviews
                            </MobileNavLink>
                            <MobileNavLink href="/#pricing">
                              Pricing
                            </MobileNavLink>
                            <MobileNavLink href="/#faqs">FAQs</MobileNavLink>
                          </div>
                          <div className="mt-8 flex flex-col gap-4">
                            <Button onClick={() => signIn()} variant="outline">
                              Log in
                            </Button>
                            <Button
                              onClick={() =>
                                useRegisterStore.getState().reset()
                              }
                              href={urls.register.start.href}
                            >
                              Register
                            </Button>
                          </div>
                        </PopoverPanel>
                      </>
                    ) : null}
                  </AnimatePresence>
                </>
              )}
            </Popover>
            <Button
              className="hidden lg:block"
              onClick={() => signIn()}
              variant="outline"
            >
              Log in
            </Button>
            <Button
              className="hidden lg:block"
              onClick={() => useRegisterStore.getState().reset()}
              href={urls.register.start.href}
            >
              Register
            </Button>
          </div>
        </Container>
      </nav>
    </header>
  );
}
